import {
  DerivedKeys,
  NetworkDisconnectedError,
  deriveKeys,
  signChallenge,
} from "@taskfortress/core"
import { VerificationType } from "@taskfortress/core/src/api/types"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import type { ReactNode } from "react"
import { useApi } from "../providers/ApiProvider"
import { AuthStorage } from "./AuthStorage"

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  email: string | null
  obfuscatedEmail: string | null
  derivedKeys: DerivedKeys | null
  hasEncryptedAuthKeys: boolean
}

interface AuthContextType extends AuthState {
  login: (
    email: string,
    password: string,
    masterKey: string,
  ) => Promise<boolean>
  loginAgain: (password: string) => Promise<boolean>
  checkEmail: (email: string) => Promise<boolean>
  requestVerification: (
    email: string,
    type?: VerificationType,
  ) => Promise<{ success: boolean; verificationToken?: string }>
  verifyCode: (
    email: string,
    verificationToken: string,
    code: string,
    type?: VerificationType,
  ) => Promise<boolean>
  signup: (
    email: string,
    password: string,
    masterKey: string,
    verificationToken: string,
    code: string,
  ) => Promise<boolean>
  resetAccount: (
    email: string,
    verificationToken: string,
    code: string,
    password: string,
    masterKey: string,
  ) => Promise<boolean>
  logout: () => Promise<void>
  restoreSession: () => Promise<void>
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  token: null,
  email: null,
  obfuscatedEmail: null,
  derivedKeys: null,
  hasEncryptedAuthKeys: false,
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>(initialState)
  const authStorage = AuthStorage.getInstance()
  const { client: apiClient } = useApi()

  // Attempt to restore session from storage
  useEffect(() => {
    const tryRestore = async () => {
      try {
        await restoreSession()
      } catch (error) {
        console.error("Failed to restore session:", error)
        setState((prev) => ({ ...prev, isLoading: false }))
      }
    }
    tryRestore()
  }, [])

  // Helper function to set auth state after successful login/signup
  const setAuthState = useCallback(
    (token: string, email: string, derivedKeys: DerivedKeys) => {
      // Set token in API client
      apiClient.setToken(token)

      // Update state
      setState({
        isAuthenticated: true,
        isLoading: false,
        token,
        email,
        obfuscatedEmail: authStorage.getObfuscatedEmail(),
        derivedKeys,
        hasEncryptedAuthKeys: true,
      })
    },
    [apiClient, authStorage],
  )

  // Helper function to setup authentication with derived keys
  const setupAuthWithDerivedKeys = useCallback(
    async (
      email: string,
      password: string,
      derivedKeys: DerivedKeys,
      token: string,
    ): Promise<boolean> => {
      try {
        // Store password in session storage
        authStorage.storePassword(password)

        // Store encrypted auth data in persistent storage
        await authStorage.storeAuthData({ token, derivedKeys, email }, password)

        // Update auth state
        setAuthState(token, email, derivedKeys)

        return true
      } catch (error) {
        console.error("Auth setup failed:", error)
        return false
      }
    },
    [authStorage, setAuthState],
  )

  // Restore session from storage
  const restoreSession = useCallback(async (): Promise<void> => {
    try {
      // Check for session password
      const password = authStorage.getPassword()
      if (!password) {
        // No password in session, but check if we have an obfuscated email
        const { obfuscatedEmail, encryptedData } =
          authStorage.getObfuscatedData()

        setState({
          ...initialState,
          isLoading: false,
          obfuscatedEmail: obfuscatedEmail || null,
          hasEncryptedAuthKeys: !!encryptedData,
        })

        return
      }

      // Try to decrypt auth data with the session password
      const authData = await authStorage.getAuthData(password)
      if (
        !authData ||
        !authData.token ||
        !authData.derivedKeys ||
        !authData.email
      ) {
        throw new Error("Failed to decrypt auth data")
      }

      // Success - retrieve session information
      const { derivedKeys, email, token } = authData

      // Validate derivedKeys and authKeyPair before proceeding
      if (
        !derivedKeys ||
        !derivedKeys.authKeyPair ||
        !derivedKeys.authKeyPair.privateKey
      ) {
        throw new Error("Invalid derived keys or auth key pair")
      }

      try {
        // Request a challenge from the server
        const challengeResponse = await apiClient.auth.challenge(email)

        if (
          !challengeResponse ||
          !challengeResponse.success ||
          !challengeResponse.challenge
        ) {
          throw new Error("Request challenge from server failed")
        }

        // Sign the challenge with the private key
        const signature = await signChallenge(
          challengeResponse.challenge,
          derivedKeys.authKeyPair.privateKey,
        )

        // Login with the API using the signed challenge
        const response = await apiClient.auth.login(email, signature)

        if (!response || !response.success || !response.token) {
          throw new Error("Failed to login with challenge response")
        }

        const newToken = response.token

        // Update stored auth data with the new token
        await authStorage.storeAuthData(
          { ...authData, token: newToken },
          password,
        )

        // Update auth state
        setAuthState(newToken, email, derivedKeys)
      } catch (error) {
        // If the error is a network error, continue with the existing token
        if (error instanceof NetworkDisconnectedError) {
          console.warn("Network disconnected, using existing token")

          // Set the API client token even if offline
          apiClient.setToken(token)

          // Update auth state with existing token
          setAuthState(token, email, derivedKeys)
        } else {
          throw error
        }
      }
    } catch (error) {
      console.error("Failed to restore session:", error)
      setState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [apiClient, authStorage, setAuthState])

  // Login again with just password (when account exists)
  const loginAgain = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }))

        // Store password in session storage
        authStorage.storePassword(password)

        // Use restoreSession to handle the rest of the authentication flow
        await restoreSession()
        return true
      } catch (error) {
        console.error("Login again failed:", error)
        setState((prev) => ({ ...prev, isLoading: false }))
        return false
      }
    },
    [authStorage, restoreSession],
  )

  // Check if email exists
  const checkEmail = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        const response = await apiClient.auth.checkEmail(email)
        return response?.exists || false
      } catch (error) {
        console.error("Email check failed:", error)
        return false
      }
    },
    [apiClient],
  )

  // Request verification code
  const requestVerification = useCallback(
    async (
      email: string,
      type: VerificationType = VerificationType.SIGNUP,
    ): Promise<{ success: boolean; verificationToken?: string }> => {
      try {
        const response = await apiClient.auth.requestVerification(email, type)
        return {
          success: !!response?.token,
          verificationToken: response?.token,
        }
      } catch (error) {
        console.error("Verification request failed:", error)
        return { success: false }
      }
    },
    [apiClient],
  )

  // Verify code
  const verifyCode = useCallback(
    async (
      email: string,
      verificationToken: string,
      code: string,
      type: VerificationType = VerificationType.SIGNUP,
    ): Promise<boolean> => {
      try {
        const response = await apiClient.auth.verifyEmail(
          email,
          verificationToken,
          code,
          type,
        )
        return response?.success || false
      } catch (error) {
        console.error("Code verification failed:", error)
        return false
      }
    },
    [apiClient],
  )

  // Login with email and password
  const login = useCallback(
    async (
      email: string,
      password: string,
      masterKey: string,
    ): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }))

        // Derive keys from password and master key
        const derivedKeys = await deriveKeys(password, masterKey)

        if (!derivedKeys) {
          return false
        }

        // Request a challenge from the server
        const challengeResponse = await apiClient.auth.challenge(email)

        if (
          !challengeResponse ||
          !challengeResponse.success ||
          !challengeResponse.challenge
        ) {
          return false
        }

        // Sign the challenge with the private key
        const signature = await signChallenge(
          challengeResponse.challenge,
          derivedKeys.authKeyPair.privateKey,
        )

        // Login with the API using the signed challenge
        const loginResponse = await apiClient.auth.login(email, signature)

        if (!loginResponse || !loginResponse.success || !loginResponse.token) {
          return false
        }

        const token = loginResponse.token

        // Setup authentication with the derived keys
        return await setupAuthWithDerivedKeys(
          email,
          password,
          derivedKeys,
          token,
        )
      } catch (error) {
        console.error("Login failed:", error)
        setState((prev) => ({ ...prev, isLoading: false }))
        return false
      }
    },
    [apiClient, setupAuthWithDerivedKeys],
  )

  // Signup with email and password
  const signup = useCallback(
    async (
      email: string,
      password: string,
      masterKey: string,
      verificationToken: string,
      code: string,
    ): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }))

        // Derive keys from master key
        const derivedKeys = await deriveKeys(password, masterKey)

        if (!derivedKeys) {
          return false
        }

        // Get the public key for sending to server
        const authPublicKey = derivedKeys.authKeyPair.publicKey

        // Sign up with the API
        const signupResponse = await apiClient.auth.signup(
          email,
          verificationToken,
          code,
          authPublicKey,
        )

        if (
          !signupResponse ||
          !signupResponse.success ||
          !signupResponse.token
        ) {
          return false
        }

        const token = signupResponse.token

        // Setup authentication with the derived keys
        return await setupAuthWithDerivedKeys(
          email,
          password,
          derivedKeys,
          token,
        )
      } catch (error) {
        console.error("Signup failed:", error)
        setState((prev) => ({ ...prev, isLoading: false }))
        return false
      }
    },
    [apiClient, setupAuthWithDerivedKeys],
  )

  // Reset account
  const resetAccount = useCallback(
    async (
      email: string,
      verificationToken: string,
      code: string,
      password: string,
      masterKey: string,
    ): Promise<boolean> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }))

        // Derive keys from password and master key
        const derivedKeys = await deriveKeys(password, masterKey)

        if (!derivedKeys) {
          return false
        }

        // Get the public key for sending to server
        const authPublicKey = derivedKeys.authKeyPair.publicKey

        // Reset the account on the server
        const resetResponse = await apiClient.auth.resetAccount(
          email,
          verificationToken,
          code,
          authPublicKey,
        )

        if (!resetResponse || !resetResponse.success || !resetResponse.token) {
          return false
        }

        const token = resetResponse.token

        // Setup authentication with the derived keys
        return await setupAuthWithDerivedKeys(
          email,
          password,
          derivedKeys,
          token,
        )
      } catch (error) {
        console.error("Failed to reset account:", error)
        setState((prev) => ({ ...prev, isLoading: false }))
        return false
      }
    },
    [apiClient, setupAuthWithDerivedKeys],
  )

  // Logout
  const logout = useCallback(async (): Promise<void> => {
    // Clear token from API client
    apiClient.setToken(null)

    // Clear auth data
    authStorage.clearAllAuth()

    // Reset state
    setState({
      ...initialState,
      isLoading: false,
    })
  }, [apiClient, authStorage])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        loginAgain,
        checkEmail,
        requestVerification,
        verifyCode,
        signup,
        resetAccount,
        logout,
        restoreSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
